/**
 * Componente: Etapa 1 - Identificação do Equipamento
 * 
 * Este componente gerencia a seleção do tipo de OS e a busca de equipamentos.
 * Migrado incrementalmente de App.js
 */

import React, { useEffect, useState } from 'react';
import { useEquipmentSearch } from '../hooks/useEquipmentSearch';
import { checkOpenOS, checkRetiradaAtivaAbertura, searchContracts } from '../services/equipmentService';

const EquipmentStep = ({
  osType,
  setOsType,
  selectedEquipment,
  setSelectedEquipment,
  onEquipmentSelect,
  onEquipmentGuardBlocked,
  setShowOpenOsNotification,
  setOpenOsNumbers,
  setShowRetiradaAtivaNotification,
  setRetiradaAtivaCodigos
}) => {
  const {
    serialSearch,
    setSerialSearch,
    suggestions,
    setSuggestions,
    isSearching,
    setIsSearching,
    hasSearched,
    setHasSearched,
    suppressNoResultsMessage,
    setSuppressNoResultsMessage,
    isEditingStep1Ref,
    previousEquipmentRef,
    searchInitiatedByEditRef,
    lastSuccessfulSearchRef,
    equipmentCacheRef,
    serialInputRef,
    searchSerialNumber,
    filterFromCache
  } = useEquipmentSearch();

  // Estado para o segmented control
  const [searchMode, setSearchMode] = useState('serie');

  // Função para limpar estados ao mudar tipo de OS
  const clearStatesOnTypeChange = () => {
    setSerialSearch('');
    setSuggestions([]);
    setHasSearched(false);
    setSuppressNoResultsMessage(false);
    isEditingStep1Ref.current = false;
    setSelectedEquipment(null);
    equipmentCacheRef.current = { prefix: '', type: '', allResults: [] };
    if (onEquipmentSelect) {
      onEquipmentSelect(null);
    }
  };

  // Debounce para pesquisa
  useEffect(() => {
    // Se o modo de busca for 'cliente', buscar contratos
    if (searchMode === 'cliente' && serialSearch.length >= 4 && osType) {
      const timer = setTimeout(async () => {
        if (isEditingStep1Ref.current || suppressNoResultsMessage || searchInitiatedByEditRef.current) {
          return;
        }
        
        setIsSearching(true);
        setHasSearched(false);
        
        try {
          const result = await searchContracts(serialSearch);
          if (result.success) {
            setSuggestions(result.suggestions);
            setHasSearched(true);
            lastSuccessfulSearchRef.current = {
              search: serialSearch.trim().toUpperCase(),
              timestamp: Date.now()
            };
          } else {
            setSuggestions([]);
            setHasSearched(true);
          }
        } catch (error) {
          console.error('Erro ao buscar contratos:', error);
          setSuggestions([]);
          setHasSearched(true);
        } finally {
          setIsSearching(false);
        }
      }, 300);
      
      return () => {
        clearTimeout(timer);
      };
    }
    
    // Se o modo de busca for 'serie', usar a lógica original
    if (searchMode === 'serie') {
      const matchesPreviousEquipment = previousEquipmentRef.current && 
        serialSearch.trim().toUpperCase() === (previousEquipmentRef.current.serie || '').trim().toUpperCase();
      
      const hasRecentSuccessfulSearch = lastSuccessfulSearchRef.current && 
        lastSuccessfulSearchRef.current.search === serialSearch.trim().toUpperCase() &&
        (Date.now() - lastSuccessfulSearchRef.current.timestamp) < 5000;
      
      if (matchesPreviousEquipment && previousEquipmentRef.current) {
        setSuggestions([previousEquipmentRef.current]);
        setHasSearched(true);
        return;
      }
      
      if (hasRecentSuccessfulSearch) {
        return;
      }
      
      if (isEditingStep1Ref.current || suppressNoResultsMessage || searchInitiatedByEditRef.current || matchesPreviousEquipment) {
        return;
      }
      
      if (serialSearch.length >= 4 && osType) {
        const timer = setTimeout(() => {
          const stillMatchesPrevious = previousEquipmentRef.current && 
            serialSearch.trim().toUpperCase() === (previousEquipmentRef.current.serie || '').trim().toUpperCase();
          
          const stillHasRecentSearch = lastSuccessfulSearchRef.current && 
            lastSuccessfulSearchRef.current.search === serialSearch.trim().toUpperCase() &&
            (Date.now() - lastSuccessfulSearchRef.current.timestamp) < 5000;
          
          if (isEditingStep1Ref.current || suppressNoResultsMessage || searchInitiatedByEditRef.current || stillMatchesPrevious || stillHasRecentSearch) {
            return;
          }
          searchSerialNumber(serialSearch, osType);
        }, 300);
        
        return () => {
          clearTimeout(timer);
        };
      } else {
        if (!isEditingStep1Ref.current && !suppressNoResultsMessage && !searchInitiatedByEditRef.current && !matchesPreviousEquipment && !hasRecentSuccessfulSearch) {
          setSuggestions([]);
          setHasSearched(false);
        }
      }
    }
  }, [serialSearch, osType, searchMode, searchSerialNumber, suppressNoResultsMessage, isEditingStep1Ref, previousEquipmentRef, searchInitiatedByEditRef, lastSuccessfulSearchRef, setSuggestions, setHasSearched, setIsSearching]);

  // Focar automaticamente no input ao selecionar tipo
  useEffect(() => {
    if (osType && !selectedEquipment && serialInputRef.current) {
      serialInputRef.current.focus();
      serialInputRef.current.select?.();
    }
  }, [osType, selectedEquipment]);

  // Focar automaticamente no input ao mudar para modo "Cliente e contrato"
  useEffect(() => {
    if (searchMode === 'cliente' && !selectedEquipment && serialInputRef.current) {
      // Pequeno delay para garantir que o input está renderizado
      setTimeout(() => {
        if (serialInputRef.current) {
          serialInputRef.current.focus();
        }
      }, 100);
    }
  }, [searchMode, selectedEquipment]);

  // Garantir que no modo estoque o searchMode seja 'serie'
  useEffect(() => {
    if (osType === 'estoque' && searchMode === 'cliente') {
      setSearchMode('serie');
    }
  }, [osType]);

  // Handler para seleção de equipamento ou contrato
  const handleEquipmentSelect = async (suggestion) => {
    isEditingStep1Ref.current = false;
    setSuppressNoResultsMessage(false);
    previousEquipmentRef.current = null;
    searchInitiatedByEditRef.current = false;
    lastSuccessfulSearchRef.current = null;
    
    // Se for um contrato (tem codigo mas não tem serie)
    if (searchMode === 'cliente' && suggestion.codigo && !suggestion.serie) {
      // Criar um objeto de contrato (sem propriedade serie para identificar como contrato)
      const contractEquipment = {
        codigo: suggestion.codigo,
        classif: suggestion.classif,
        isContract: true // Flag para identificar que é um contrato
      };
      setSelectedEquipment(contractEquipment);
      setSerialSearch(suggestion.classif || suggestion.codigo);
      setSuggestions([]);
      if (onEquipmentSelect) {
        onEquipmentSelect(contractEquipment);
      }
      return;
    }
    
    // Se for um equipamento (tem serie), verificar OS aberta e retirada ativa
    if (suggestion.serie) {
      try {
        const [openResult, retiradaResult] = await Promise.all([
          checkOpenOS(suggestion.serie),
          checkRetiradaAtivaAbertura(suggestion.serie)
        ]);

        const hasOpen = openResult.success && openResult.hasOpenOs;
        const hasRetirada =
          retiradaResult.success &&
          retiradaResult.hasRetiradaAtiva &&
          Array.isArray(retiradaResult.codigos) &&
          retiradaResult.codigos.length > 0;

        if (hasOpen || hasRetirada) {
          if (hasOpen) {
            setOpenOsNumbers(openResult.osNumbers);
            setShowOpenOsNotification(true);
          } else {
            setOpenOsNumbers([]);
            setShowOpenOsNotification(false);
          }
          if (hasRetirada) {
            setRetiradaAtivaCodigos(retiradaResult.codigos);
            setShowRetiradaAtivaNotification(true);
          } else {
            setRetiradaAtivaCodigos([]);
            setShowRetiradaAtivaNotification(false);
          }
          if (onEquipmentGuardBlocked) {
            onEquipmentGuardBlocked(suggestion);
          }
          return;
        }

        setSelectedEquipment(suggestion);
        setSerialSearch(suggestion.serie);
        setSuggestions([]);
        if (onEquipmentSelect) {
          onEquipmentSelect(suggestion);
        }
      } catch (error) {
        console.error('Erro ao verificar equipamento:', error);
        setSelectedEquipment(suggestion);
        setSerialSearch(suggestion.serie);
        setSuggestions([]);
        if (onEquipmentSelect) {
          onEquipmentSelect(suggestion);
        }
      }
    }
  };

  // Handler para mudança no input
  const handleInputChange = (e) => {
    if (selectedEquipment) {
      setSelectedEquipment(null);
      if (onEquipmentSelect) {
        onEquipmentSelect(null);
      }
    }
    const value = e.target.value.replace(/\s+/g, ' ').trimStart().toUpperCase();
    setSerialSearch(value);
    setSuppressNoResultsMessage(false);
    isEditingStep1Ref.current = false;
    previousEquipmentRef.current = null;
    searchInitiatedByEditRef.current = false;
    lastSuccessfulSearchRef.current = null;
    setShowOpenOsNotification(false);
    setOpenOsNumbers([]);
    setShowRetiradaAtivaNotification(false);
    setRetiradaAtivaCodigos([]);
  };

  return (
    <div style={{
      background: 'var(--modal-bg, #ffffff)',
      borderRadius: '12px',
      padding: '20px',
      border: '1px solid var(--border-subtle, #e8e8e8)',
      boxShadow: '0 2px 8px rgba(124, 77, 255, 0.08)',
      flex: '0 0 500px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '14px',
        fontWeight: 600,
        fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
        color: 'var(--text-body, #2d3748)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
      }}>
        Etapa 1: Identificação do Equipamento
      </h3>

      {/* Seleção de Tipo de OS */}
      <div style={{ marginBottom: '20px' }}>
        <label style={{
          display: 'block',
          marginBottom: '8px',
          fontSize: '12px',
          fontWeight: 500,
          fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
          color: 'var(--text-muted, #4a5568)'
        }}>
          Tipo de OS <span style={{ color: '#e53e3e' }}>*</span>
        </label>
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={() => {
              setOsType('contrato');
              clearStatesOnTypeChange();
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: osType === 'contrato' ? '#7c4dff' : 'var(--bg-surface-alt, #f7fafc)',
              color: osType === 'contrato' ? '#ffffff' : 'var(--text-muted, #4a5568)',
              border: osType === 'contrato' ? '2px solid #7c4dff' : '2px solid var(--border-subtle, #e2e8f0)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: osType === 'contrato' ? 600 : 500,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px' }}>📄</span>
            Contrato
          </button>
          <button
            onClick={() => {
              setOsType('estoque');
              clearStatesOnTypeChange();
            }}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: osType === 'estoque' ? '#7c4dff' : 'var(--bg-surface-alt, #f7fafc)',
              color: osType === 'estoque' ? '#ffffff' : 'var(--text-muted, #4a5568)',
              border: osType === 'estoque' ? '2px solid #7c4dff' : '2px solid var(--border-subtle, #e2e8f0)',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: osType === 'estoque' ? 600 : 500,
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px'
            }}
          >
            <span style={{ fontSize: '16px' }}>📦</span>
            Estoque
          </button>
        </div>
      </div>

      {/* Campo de Pesquisa de Série */}
      {osType && (
        <div style={{ marginBottom: '12px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '12px',
            fontWeight: 500,
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: 'var(--text-muted, #4a5568)'
          }}>
            Número de Série ou Patrimônio <span style={{ color: '#e53e3e' }}>*</span>
          </label>
          
          {/* Segmented Control - Só exibir quando for modo contrato */}
          {osType === 'contrato' && (
          <div style={{
            marginBottom: '12px',
            display: 'flex',
            background: 'var(--bg-surface-alt, #f0f0f0)',
            borderRadius: '8px',
            padding: '3px',
            position: 'relative',
            width: '50%',
            maxWidth: '50%',
            height: '23px'
          }}>
            {/* Indicador animado */}
            <div
              style={{
                position: 'absolute',
                top: '3px',
                left: searchMode === 'serie' ? '3px' : 'calc(50% + 3px)',
                width: 'calc(50% - 3px)',
                height: 'calc(100% - 6px)',
                background: 'var(--modal-bg, #ffffff)',
                borderRadius: '6px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                zIndex: 1
              }}
            />
            
            {/* Botão Série ou patrimônio */}
            <button
              type="button"
              onClick={() => {
                setSearchMode('serie');
                setSerialSearch('');
                setSuggestions([]);
                setHasSearched(false);
                setSelectedEquipment(null);
                if (onEquipmentSelect) {
                  onEquipmentSelect(null);
                }
              }}
              style={{
                flex: 1,
                position: 'relative',
                zIndex: 2,
                padding: '4px 12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                fontSize: '8.5px',
                fontWeight: searchMode === 'serie' ? 600 : 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: searchMode === 'serie' ? 'var(--text-body, #2d3748)' : 'var(--text-muted, #718096)',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                outline: 'none'
              }}
            >
              Série ou patrimônio
            </button>
            
            {/* Botão Cliente e contrato */}
            <button
              type="button"
              onClick={() => {
                setSearchMode('cliente');
                setSerialSearch('');
                setSuggestions([]);
                setHasSearched(false);
                setSelectedEquipment(null);
                if (onEquipmentSelect) {
                  onEquipmentSelect(null);
                }
              }}
              style={{
                flex: 1,
                position: 'relative',
                zIndex: 2,
                padding: '4px 12px',
                background: 'transparent',
                border: 'none',
                borderRadius: '6px',
                fontSize: '8.5px',
                fontWeight: searchMode === 'cliente' ? 600 : 500,
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: searchMode === 'cliente' ? 'var(--text-body, #2d3748)' : 'var(--text-muted, #718096)',
                cursor: 'pointer',
                transition: 'color 0.2s ease',
                outline: 'none'
              }}
            >
              Cliente e contrato
            </button>
          </div>
          )}
          
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <input
              type="text"
              ref={serialInputRef}
              value={selectedEquipment ? (selectedEquipment.classif || selectedEquipment.serie) : serialSearch}
              onChange={handleInputChange}
              placeholder="Pesquise aqui..."
              style={{
                width: '100%',
                padding: '10px 12px',
                paddingRight: isSearching ? '40px' : '12px',
                border: '2px solid var(--border-subtle, #e2e8f0)',
                borderRadius: '8px',
                fontSize: '13px',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                color: 'var(--text-body, #2d3748)',
                outline: 'none',
                transition: 'all 0.2s ease',
                background: 'var(--modal-bg, #ffffff)'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7c4dff'}
              onBlur={(e) => e.target.style.borderColor = 'var(--border-subtle, #e2e8f0)'}
            />
            {isSearching && (
              <div style={{
                position: 'absolute',
                right: '12px',
                width: '16px',
                height: '16px',
                border: '2px solid #7c4dff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}></div>
            )}
          </div>
          {serialSearch.length > 0 && serialSearch.length < 4 && (
            <p style={{
              margin: '6px 0 0 0',
              fontSize: '11px',
              fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
              color: 'var(--text-muted, #718096)'
            }}>
              Digite mais {4 - serialSearch.length} caractere(s) para buscar
            </p>
          )}
        </div>
      )}

      {/* Sugestões de Série ou Contratos */}
      {suggestions.length > 0 && !selectedEquipment && (
        <div style={{
          marginTop: '12px',
          border: '1px solid var(--border-subtle, #e2e8f0)',
          borderRadius: '8px',
          background: 'var(--modal-bg, #ffffff)',
          maxHeight: '280px',
          overflowY: 'auto'
        }}>
          {suggestions.map((suggestion, index) => (
            <div
              key={index}
              onClick={() => handleEquipmentSelect(suggestion)}
              style={{
                padding: '12px',
                borderBottom: index < suggestions.length - 1 ? '1px solid var(--border-subtle, #f0f0f0)' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
                fontSize: '12px',
                color: 'var(--text-body, #2d3748)'
              }}
              className="open-os-list-item"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-surface-alt, #f7fafc)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {searchMode === 'cliente' && suggestion.classif ? (
                <div style={{ fontWeight: 600 }}>
                  {suggestion.classif}
                </div>
              ) : (
                <>
                  <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                    {suggestion.serie} {suggestion.patrimonio && `• ${suggestion.patrimonio}`}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted, #718096)' }}>
                    {suggestion.produto}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Mensagem de erro */}
      {(() => {
        const hasPreviousEquipment = !!previousEquipmentRef.current;
        const matchesPreviousEquipment = previousEquipmentRef.current && 
          serialSearch.trim().toUpperCase() === (previousEquipmentRef.current.serie || '').trim().toUpperCase();
        
        if (hasPreviousEquipment || isEditingStep1Ref.current || matchesPreviousEquipment || suppressNoResultsMessage) {
          return null;
        }
        
        const currentSearchUpper = serialSearch.trim().toUpperCase();
        const lastSearch = lastSuccessfulSearchRef.current;
        const timeSinceSearch = lastSearch ? Date.now() - lastSearch.timestamp : null;
        const recentSuccessfulSearch = lastSearch && 
          lastSearch.search === currentSearchUpper &&
          timeSinceSearch !== null &&
          timeSinceSearch < 10000;
        
        if (recentSuccessfulSearch) {
          return null;
        }
        
        const justSearched = hasSearched && suggestions.length === 0 && !isSearching;
        if (justSearched && serialSearch.length >= 4) {
          return null;
        }
        
        const shouldShowError = serialSearch.length >= 4 && 
                               suggestions.length === 0 && 
                               !isSearching && 
                               hasSearched &&
                               !justSearched;
        
        return shouldShowError ? (
          <div
            className="open-os-error-banner"
            style={{
            marginTop: '12px',
            padding: '12px',
            background: '#fff5f5',
            border: '1px solid #feb2b2',
            borderRadius: '8px',
            fontSize: '12px',
            fontFamily: "'Geist Variable', 'Geist', system-ui, sans-serif",
            color: '#c53030'
          }}>
            {searchMode === 'cliente' 
              ? 'Nenhum contrato encontrado com este termo de busca.'
              : 'Nenhum equipamento encontrado com esta série/patrimônio.'}
          </div>
        ) : null;
      })()}
    </div>
  );
};

export default EquipmentStep;

